import { CompanyContactId, Dtos, SuretyAccountId } from "@inrev/common";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRequest } from "../../utils/request";
import { accountCompaniesQueryKey, accountQueryKey } from "./account.keys";

export const useDeleteAccountCompany = (accountId: SuretyAccountId) => {
	const { _delete } = useRequest();
	const queryClient = useQueryClient();

	const companiesQueryKey = accountCompaniesQueryKey(accountId);

	const mutation = useMutation({
		mutationFn: async ({ companyId }: { companyId: CompanyContactId; onSuccess?: () => void }) => {
			await _delete(`/v2/surety/accounts/${accountId}/companies/${companyId}`, undefined, "none");
		},
		onMutate: async ({ companyId }) => {
			await queryClient.cancelQueries({ queryKey: companiesQueryKey });
			const previousCompanies =
				queryClient.getQueryData<Dtos.Company.Get.Response[]>(companiesQueryKey);

			queryClient.setQueryData<Dtos.Company.Get.Response[]>(companiesQueryKey, (old) => {
				if (!old) return [];
				return old.filter((c) => c.id !== companyId);
			});

			return { previousCompanies };
		},
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: accountQueryKey(accountId) });
			variables.onSuccess?.();
		},
		onError: (_error, _variables, context) => {
			if (context?.previousCompanies) {
				queryClient.setQueryData(companiesQueryKey, context.previousCompanies);
			}
		},
	});

	return {
		deleteAccountCompany: mutation.mutate,
		deleteAccountCompanyIsLoading: mutation.isPending,
		deleteAccountCompanyError: mutation.error,
	};
};
